'use client'

import { useEffect, useRef } from 'react'
import type { DehydratedState } from '@tanstack/react-query'
import { Tooltip } from '@skop-app/lib-front'

import AccountActivityListener from '@/components/accountLifecycle/AccountActivityListener'
import AuthEventsListener from '@/components/listeners/AuthEventsListener'
import AutoLogoutListener from '@/components/listeners/AutoLogoutListener'
import ImpersonationSyncListener from '@/components/listeners/ImpersonationSyncListener'
import { DrawerRegistryProvider } from '@/components/drawers/drawer.provider'
import { ModalRegistryProvider } from '@/components/modals/modal.provider'
import { DataProvider } from '@/context/DataContext'
import { QuoteProvider } from '@/context/QuoteContext'
import { ReviewProvider } from '@/context/ReviewContext'
import {
  type SessionRuntime,
  SessionRuntimeProvider,
} from '@/context/SessionRuntimeContext'
import { UserWrapper } from '@/context/UserContext'
import { useImpersonationControls } from '@/hooks/useImpersonationControls'
import { clearRedirectAttempted } from '@/services/auth/publicAuthRedirectMarker.client'
import { captureSessionEvent } from '@/services/auth/sessionTelemetry.client'

function InvalidImpersonationCookieCleanup({ enabled }: { enabled: boolean }) {
  const cleanupTriggeredRef = useRef(false)
  const { stopImpersonation: stopImpersonationSession } =
    useImpersonationControls()

  useEffect(() => {
    if (!enabled) {
      cleanupTriggeredRef.current = false
      return
    }
    if (cleanupTriggeredRef.current) return
    cleanupTriggeredRef.current = true

    captureSessionEvent('impersonation_denied', 'stale_cookie_cleared')
    void stopImpersonationSession({
      reason: 'stale_cookie_cleared',
      silent: true,
    })
  }, [enabled, stopImpersonationSession])

  return null
}

function PublicAuthRedirectMarkerCleanup() {
  useEffect(() => {
    clearRedirectAttempted()
  }, [])

  return null
}

export function RestrictedAppProviders({
  children,
  dehydratedState,
  sessionRuntime,
  shouldClearInvalidImpersonationCookie = false,
}: {
  children: React.ReactNode
  dehydratedState?: DehydratedState
  sessionRuntime: SessionRuntime
  shouldClearInvalidImpersonationCookie?: boolean
}) {
  return (
    <SessionRuntimeProvider value={sessionRuntime}>
      <DataProvider
        key={sessionRuntime.sessionScopeKey}
        dehydratedState={dehydratedState}
      >
        <UserWrapper>
          <AuthEventsListener />
          <AccountActivityListener />
          <AutoLogoutListener />
          <ImpersonationSyncListener />
          <PublicAuthRedirectMarkerCleanup />
          <InvalidImpersonationCookieCleanup
            enabled={shouldClearInvalidImpersonationCookie}
          />
          <ReviewProvider>
            <QuoteProvider>
              <ModalRegistryProvider>
                <DrawerRegistryProvider respectAppHeader>
                  <Tooltip.Provider delayDuration={50}>
                    {children}
                  </Tooltip.Provider>
                </DrawerRegistryProvider>
              </ModalRegistryProvider>
            </QuoteProvider>
          </ReviewProvider>
        </UserWrapper>
      </DataProvider>
    </SessionRuntimeProvider>
  )
}
